import { useCallback, useEffect, useMemo, useRef } from 'react'
import { DynamicDataSheetGrid, createContextMenuComponent, createTextColumn, keyColumn } from 'react-datasheet-grid'
import type { CellProps, Column, ContextMenuComponentProps, ContextMenuItem, DataSheetGridRef } from 'react-datasheet-grid'
import 'react-datasheet-grid/dist/style.css'
// 그리드는 DynamicDataSheetGrid — 기본 DataSheetGrid(Static)는 처음 받은 columns·콜백을 계속 써서 행 끝 칸·점검 표시·공동 편집 표시가 안 바뀐다
import type { SpecMachine } from '../../types'
import { PAD, dropBlankRows, useLeaveBlankRow } from './blankRows'
import { seriesValue } from './fillSeries'
import { renamePrefix } from './masterView'
import type { MasterIssues } from './masterView'
import { newKey } from './model'
import { ViewerChips } from './Presence'
import { cellMarks, markClass } from './presenceMarks'
import { blankMachine } from './specDoc'
import { useF2Edit } from './useF2Edit'
import { useFillHandle } from './useFillHandle'
import type { Sel } from './useFillHandle'
import { useLocate } from './useLocate'
import type { SpecViewer } from './useSpecDraft'

// 인터페이스 시트 — [마스터데이터] 탭의 본문. 인터페이스 한 대 = 한 행 (인터페이스 ID·설명). 디바이스 시트처럼 엑셀식으로 적고,
// 행 끝 [항목] 으로 항목 편집(전송코드·태그)을 연다. 아래 빈 행에 한 칸이라도 적으면 새 인터페이스, 칸을 다 지운 행은 없어진다
// (blankRows — 항목이 있는 인터페이스는 남긴다).
// 인터페이스 ID 를 바꾸면 그 코드로 시작하던 전송코드 앞부분이 따라 바뀐다 (110_CNT → 111_CNT).
// 우클릭 [인터페이스 복제] 는 항목·태그 연결까지 복사 — 같은 구성 인터페이스는 복제 후 인터페이스 ID만 바꾸면 된다.

const ROW_HEIGHT = 32
const PAD_ROWS = 3

interface MachineRow { key: string; from?: string; code: string; name: string; description: string }

const COLUMN_FIELDS = ['code', 'name', 'description'] as const

const toRow = (m: SpecMachine): MachineRow => ({ key: m.key, code: m.code, name: m.name ?? '', description: m.description })
const blankRow = (key: string): MachineRow => ({ key, code: '', name: '', description: '' })
const isBlank = (r: MachineRow) => !r.code.trim() && !r.name.trim() && !r.description.trim()

const MENU_LABELS: Record<ContextMenuItem['type'], string> = {
  INSERT_ROW_BELLOW: '아래에 인터페이스 삽입',
  DUPLICATE_ROW: '인터페이스 복제 (항목·태그 포함)',
  DELETE_ROW: '인터페이스 삭제',
  DUPLICATE_ROWS: '선택한 인터페이스 복제',
  DELETE_ROWS: '선택한 인터페이스 삭제',
  COPY: '복사',
  CUT: '잘라내기',
  PASTE: '붙여넣기',
}
const ContextMenuBase = createContextMenuComponent(item => (
  <>{MENU_LABELS[item.type]}{'fromRow' in item && <span className="text-gray-400 ml-1">({item.fromRow + 1}~{item.toRow + 1})</span>}</>
))
const ContextMenu = (props: ContextMenuComponentProps) => <ContextMenuBase {...props} />

const text = () => createTextColumn<string>({
  deletedValue: '', continuousUpdates: false,
  parseUserInput: v => v, parsePastedValue: v => v.trim(), formatBlurredInput: v => v ?? '',
})

interface ActionData {
  issues: MasterIssues
  onEditItems: (key: string) => void
  others: (key: string) => SpecViewer[]
}
function ActionCell({ rowData, columnData }: CellProps<MachineRow, ActionData>) {
  const info = columnData.issues.machine[rowData.key]
  if (!info) return null
  const stop = (e: React.MouseEvent) => e.stopPropagation() // 그리드가 칸 선택으로 가져가지 않게
  return (
    <div className="w-full h-full flex items-center gap-1 px-1.5 text-xs min-w-0" onMouseDown={stop}>
      <button onClick={() => columnData.onEditItems(rowData.key)} title="전송코드와 태그 편집 (Ctrl+Shift+E)"
        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-accent hover:bg-blue-50 whitespace-nowrap">
        항목 <span className={info.items ? 'text-gray-600' : 'text-gray-400'}>{info.items}</span>
        {info.unbound > 0 && <span className="text-gray-500" title="태그를 연결하지 않은 항목">· 미연결 {info.unbound}</span>}
        {info.missing > 0 && <span className="text-red-600 font-medium" title="디바이스 명세에 없는 태그">· 명세에 없음 {info.missing}</span>}
        {info.dup > 0 && <span className="text-amber-600" title="다른 항목과 전송코드가 같은 항목 (항목 편집에서 노랗게)">· 코드 겹침 {info.dup}</span>}
      </button>
      <span className="ml-auto pl-1 min-w-0 overflow-hidden"><ViewerChips viewers={columnData.others(rowData.key)} /></span>
    </div>
  )
}

/** 바깥(단축키·검색)에서 쓰는 손잡이 */
export interface MachineGridHandle {
  activeKey: () => string | null
  /** 그 인터페이스 행의 칸으로 커서를 옮기고 화면에 보이게 스크롤 */
  focusMachine: (key: string, field?: 'code' | 'name' | 'description') => void
}

export default function MachineGrid({ machines, issues, viewers, onChange, onEditItems, onNotice, onLocate, handleRef }: {
  machines: SpecMachine[]
  issues: MasterIssues
  viewers: SpecViewer[] // 나를 뺀 접속자
  onChange: (machines: SpecMachine[]) => void
  onEditItems: (key: string) => void
  onNotice: (message: string) => void
  onLocate: (machineKey: string | null, field: string | null, editing: boolean) => void
  handleRef?: (handle: MachineGridHandle | null) => void
}) {
  const gridRef = useRef<DataSheetGridRef>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  useF2Edit(gridRef)

  const display = useMemo(
    () => [...machines.map(toRow), ...Array.from({ length: PAD_ROWS }, (_, j) => blankRow(`${PAD}${machines.length + j}`))],
    [machines],
  )
  const displayRef = useRef(display)
  const handleRefRef = useRef(handleRef)
  useEffect(() => { displayRef.current = display; handleRefRef.current = handleRef })
  useEffect(() => {
    const register = handleRefRef.current
    register?.({
      activeKey: () => {
        const cell = gridRef.current?.activeCell
        const key = cell ? displayRef.current[cell.row]?.key : undefined
        return key && !key.startsWith(PAD) ? key : null
      },
      focusMachine: (key, field) => {
        const grid = gridRef.current
        const row = displayRef.current.findIndex(r => r.key === key)
        if (!grid || row < 0) return
        grid.setActiveCell({ col: Math.max(0, COLUMN_FIELDS.indexOf(field ?? 'code')), row })
        setTimeout(() => wrapRef.current?.querySelector('.dsg-active-cell')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60)
      },
    })
    return () => register?.(null)
  }, [])

  const byKey = useMemo(() => new Map(machines.map(m => [m.key, m])), [machines])

  const locateCell = useLocate(wrapRef, onLocate)
  const fill = useFillHandle({
    wrapRef, rowHeight: ROW_HEIGHT,
    activeCell: () => gridRef.current?.activeCell ?? null,
    increments: sel => ctrl => ctrl && COLUMN_FIELDS.slice(sel.min.col, sel.max.col + 1).some(f =>
      seriesValue(displayRef.current.slice(sel.min.row, sel.max.row + 1).map(r => r[f]), 1, true) != null),
    onFill: (sel, target, ctrl) => fillDown(sel, target, ctrl),
  })

  // 원본 행(sel)을 target 행까지 — 빈 행·표 아래까지 끌면 새 인터페이스 (항목은 비어 있다. 항목까지 복사하려면 우클릭 복제)
  const fillDown = (sel: Sel, target: number, ctrl: boolean) => {
    const rows = [...displayRef.current]
    while (rows.length <= target) rows.push(blankRow(`${PAD}${rows.length}`))
    for (const field of COLUMN_FIELDS.slice(sel.min.col, sel.max.col + 1)) {
      const src = rows.slice(sel.min.row, sel.max.row + 1).map(r => r[field])
      for (let row = sel.max.row + 1; row <= target; row++) {
        const k = row - sel.max.row
        rows[row] = { ...rows[row], [field]: seriesValue(src, k, ctrl) ?? src[(k - 1) % src.length] }
      }
    }
    handleChange(rows)
    requestAnimationFrame(() => gridRef.current?.setSelection({ min: { col: sel.min.col, row: sel.min.row }, max: { col: sel.max.col, row: target } }))
  }

  // 행 → 인터페이스. 인터페이스 ID가 바뀌면 전송코드 앞부분을 따라 바꾸고, 복제한 행은 원본 항목을 새 키로 이어받는다
  const handleChange = (rows: MachineRow[]) => {
    const next = dropBlankRows(rows, displayRef.current, r => isBlank(r) && !byKey.get(r.key)?.items.length).map(row => {
      const existing = byKey.get(row.key)
      const origin = row.from ? byKey.get(row.from) : undefined
      const key = row.key.startsWith(PAD) ? newKey() : row.key
      const src: SpecMachine = existing
        ?? (origin ? { ...origin, key, items: origin.items.map(i => ({ ...i, key: newKey() })) } : blankMachine(key))
      if (existing && existing.code === row.code && (existing.name ?? '') === row.name && existing.description === row.description) return existing
      const from = existing ? existing.code : origin?.code ?? ''
      return {
        ...src, code: row.code, name: row.name, description: row.description,
        items: from && from !== row.code ? src.items.map(i => ({ ...i, code: renamePrefix(i.code, from, row.code) })) : src.items,
      }
    })
    const keptKeys = new Set(next.map(m => m.key))
    const removed = machines.filter(m => !keptKeys.has(m.key))
    onChange(next)
    if (removed.length > 0) onNotice(`인터페이스 ${removed.length}개를 지웠습니다. Ctrl+Z 로 되돌릴 수 있습니다.`)
  }

  const leaveRow = useLeaveBlankRow(gridRef, displayRef, r => isBlank(r) && byKey.has(r.key) && !byKey.get(r.key)?.items.length,
    key => onChange(machines.filter(m => m.key !== key)))

  const remote = useMemo(() => cellMarks(viewers, 'machines', v => v.cursor?.device), [viewers])
  const others = useCallback((key: string) => viewers.filter(v => v.cursor?.device === key && (v.cursor.mode === 'machines' || v.cursor.mode === 'items')), [viewers])

  const columns = useMemo<Column<MachineRow>[]>(() => {
    const cls = (field: string, mono = false) => ({ rowData }: { rowData: MachineRow }) =>
      [markClass(remote, rowData.key, field), mono ? 'font-mono' : ''].filter(Boolean).join(' ') || undefined
    return [
      {
        ...keyColumn<MachineRow, 'code'>('code', text()),
        title: <span title="바꾸면 전송코드 앞부분도 같이 바뀝니다">인터페이스 ID</span>,
        basis: 140, grow: 1, minWidth: 100, cellClassName: cls('code', true),
      },
      { ...keyColumn<MachineRow, 'name'>('name', text()), title: '이름', basis: 180, grow: 1, minWidth: 100, cellClassName: cls('name') },
      { ...keyColumn<MachineRow, 'description'>('description', text()), title: '설명', basis: 320, grow: 3, minWidth: 140, cellClassName: cls('description') },
      {
        id: 'actions', title: '', basis: 260, grow: 0, minWidth: 160, disabled: true,
        component: ActionCell as Column<MachineRow, ActionData>['component'],
        columnData: { issues, onEditItems, others },
        copyValue: () => null, pasteValue: ({ rowData }) => rowData, deleteValue: ({ rowData }) => rowData,
      } as Column<MachineRow, ActionData>,
    ] as Column<MachineRow>[]
  }, [issues, onEditItems, others, remote])

  return (
    // isolate — 표 안의 z-index 가 위의 sticky 툴바와 겨루지 않게 (DeviceGrid 와 같게)
    <div ref={wrapRef} className="spec-grid device-grid relative isolate bg-surface rounded-xl border border-gray-200 overflow-hidden">
      <DynamicDataSheetGrid<MachineRow>
        ref={gridRef}
        onActiveCellChange={({ cell }) => {
          fill.onActiveCell(cell, !!gridRef.current?.selection)
          const key = cell ? display[cell.row]?.key : undefined
          leaveRow(cell)
          locateCell(key && !key.startsWith(PAD) ? key : null, cell ? COLUMN_FIELDS[cell.col] ?? null : null)
        }}
        onSelectionChange={({ selection }) => fill.onSelection(selection)}
        onScroll={fill.place}
        value={display}
        onChange={handleChange}
        columns={columns}
        rowKey="key"
        createRow={() => blankRow(newKey())}
        duplicateRow={({ rowData }) => ({ ...rowData, key: newKey(), from: rowData.from ?? rowData.key })}
        rowClassName={({ rowData }) => (rowData.key.startsWith(PAD) ? 'device-row-pad' : undefined)}
        rowHeight={ROW_HEIGHT}
        headerRowHeight={ROW_HEIGHT}
        height={ROW_HEIGHT * (display.length + 1) + 2}
        disableSmartDelete
        disableExpandSelection
        addRowsComponent={false}
        contextMenuComponent={ContextMenu}
      />
      {fill.element}
    </div>
  )
}
